import { useState } from "react";

import { Ds } from "@accurx/design";
import { PatientExternalId } from "@accurx/native";
import { InlineError } from "domains/message-component/components/InlineError/InlineError";
import { useCompose } from "domains/message-component/context";
import { ContactDetail } from "domains/message-component/reducer.types";

import { ContactDetailsListContent } from "./ContactDetailsListContent";
import { CustomContactFormContent } from "./CustomContactFormContent";
import { StyledMinWidthFlex } from "./RecipientSelector.styles";
import { RecipientSelectorButton } from "./RecipientSelectorButton";
import { RecipientSelectorButtonContent } from "./RecipientSelectorButtonContent";

type RecipientSelectorProps = {
    patientExternalIds: PatientExternalId[];
    shouldDisableEmail: boolean;
    contactDetails: ContactDetail[];
    forceOpenSide?: "top" | "bottom";
};

export const RecipientSelector = ({
    patientExternalIds,
    contactDetails,
    shouldDisableEmail,
    forceOpenSide,
}: RecipientSelectorProps) => {
    const { state } = useCompose();

    const [isOpen, setIsOpen] = useState(false);

    const [showCustomContactDetailsForm, setShowCustomContactDetailsForm] =
        useState(false);

    const [customInputValue, setCustomInputValue] = useState(
        state.contactDetails.method !== "None" &&
            state.contactDetails.origin === "UserInput"
            ? state.contactDetails.value
            : "",
    );

    const hasError = !!state.topLevelErrors?.noContactDetails;

    return (
        <Ds.Popover
            open={isOpen}
            onOpenChange={(open) => {
                setShowCustomContactDetailsForm(
                    state.contactDetails.method !== "None" &&
                        state.contactDetails.origin === "UserInput",
                );
                setIsOpen(open);
            }}
        >
            <StyledMinWidthFlex
                gap="0.5"
                alignItems={hasError ? "baseline" : "center"}
            >
                <Ds.Text size="small" color="zinc">
                    To:
                </Ds.Text>
                <StyledMinWidthFlex
                    flexDirection={"column"}
                    gap="0.25"
                    alignItems={"start"}
                >
                    <Ds.Popover.Trigger asChild={true}>
                        <RecipientSelectorButton
                            onClick={() => null}
                            isError={hasError}
                        >
                            <RecipientSelectorButtonContent
                                patientExternalIds={patientExternalIds}
                            />
                        </RecipientSelectorButton>
                    </Ds.Popover.Trigger>
                    {hasError && (
                        <InlineError>
                            Enter patient's contact details to send message
                        </InlineError>
                    )}
                </StyledMinWidthFlex>
            </StyledMinWidthFlex>

            <Ds.Popover.Content
                align="start"
                aria-labelledby="recipient-selector"
                side={forceOpenSide}
            >
                {contactDetails.length > 0 && !showCustomContactDetailsForm ? (
                    <ContactDetailsListContent
                        contactDetails={contactDetails}
                        patientExternalIds={patientExternalIds}
                        setIsOpen={setIsOpen}
                        setShowCustomContactDetailsForm={
                            setShowCustomContactDetailsForm
                        }
                        shouldDisableEmail={shouldDisableEmail}
                    />
                ) : (
                    <CustomContactFormContent
                        contactDetails={contactDetails}
                        patientExternalIds={patientExternalIds}
                        setIsOpen={setIsOpen}
                        setShowCustomContactDetailsForm={
                            setShowCustomContactDetailsForm
                        }
                        customInputValue={customInputValue}
                        setCustomInputValue={setCustomInputValue}
                        shouldDisableEmail={shouldDisableEmail}
                    />
                )}
            </Ds.Popover.Content>
        </Ds.Popover>
    );
};
